import { ICellRenderer, ICellRendererParams } from '@ag-grid-community/core';
import { MaturityAmountEntry } from '../../cell-renderers/maturity-amount-entry';
import { DealConditionsSummary } from '../../cell-renderers/deal-conditions';
import {
  allowOnlyWholePositiveNumbers,
  formatOrderQuantityForCell,
  hasOrderAmountApplied,
  hasOrderConditions,
  isFullParOrderAmountApplied,
} from '../deal-series.util';
import { isReconfirmResetTarget, isReconfirmRowResetEnabled, resolveSplitRowReconfirmEligible } from '../../deal-detail/deal-reconfirm.utils';
import { MaturityOrder, OrderStatusEnum } from '../deal-series.types';
import { isSplitRowEditable, splitRowHasEditsFromAcceptedBaseline } from '../split-order.util';
import { SplitOrderRow } from './split-order-modal.types';
import { applyQuantityValidationCellClasses } from '../deal-series.quantity-validation';
import { QuantityCellValidationState } from '../deal-series.types';

DealConditionsSummary;
MaturityAmountEntry;

export interface SplitOrderRemoveCellParams extends ICellRendererParams<SplitOrderRow> {
  onRemoveRow: (rowId: string) => void;
}

export class SplitOrderRemoveCellRenderer implements ICellRenderer {
  private params!: SplitOrderRemoveCellParams;

  private eGui = document.createElement('div');

  getGui(): HTMLElement {
    return this.eGui;
  }

  init(params: SplitOrderRemoveCellParams): void {
    this.eGui.style.cssText =
      'display:flex;align-items:center;justify-content:flex-end;width:100%;height:100%;padding-right:8px;box-sizing:border-box';
    this.refresh(params);
  }

  refresh(params: SplitOrderRemoveCellParams): boolean {
    this.params = params;
    this.eGui.replaceChildren();
    const rowId = params.data?.rowId;
    if (!rowId) {
      return true;
    }
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'split-remove-row-btn';
    removeBtn.setAttribute('aria-label', 'Remove split row');
    removeBtn.textContent = '−';
    removeBtn.onclick = (event) => {
      event.stopPropagation();
      this.params.onRemoveRow(rowId);
    };
    this.eGui.append(removeBtn);
    return true;
  }
}

export interface SplitOrderAmountCellParams extends ICellRendererParams<SplitOrderRow> {
  onAmountCommit: (rowId: string, value: string) => void;
  /** Called on each keystroke so Save Split eligibility can re-evaluate before blur. */
  onAmountDraft?: (rowId: string, value: string) => void;
  /** Returns the source maturity's par amount; called at render time so the modal always has the latest value. */
  getMaturityAmount: () => string;
  onAllClicked: (rowId: string, isAll: boolean) => void;
  getValidationState: (rowId: string, amount: string) => QuantityCellValidationState;
  isParentAllActive?: () => boolean;
  getParentAmountTotal?: () => string;
}

export class SplitOrderAmountCellRenderer implements ICellRenderer {
  private params: SplitOrderAmountCellParams;

  private eGui: HTMLElement;

  private maturityAmountEntry: MaturityAmountEntry;

  private readonly allEventHandler = (event: CustomEvent<boolean>) => {
    const { data } = this.params;
    if (!data || data.rowType === 'source') return;
    this.params.onAllClicked(data.rowId, event.detail);
  };

  private readonly changeEventHandler = (event: CustomEvent<string>) => {
    const { data } = this.params;
    if (!data || data.rowType === 'source') return;
    const value = allowOnlyWholePositiveNumbers(event.detail);
    this.params.onAmountCommit(data.rowId, value);
    this.applyValidationClasses(data.rowId, value);
  };

  getGui(): HTMLElement {
    return this.eGui;
  }

  init(params: SplitOrderAmountCellParams): void {
    this.params = params;
    this.maturityAmountEntry = document.createElement(
      'maturity-amount-entry',
    ) as MaturityAmountEntry;
    this.maturityAmountEntry.addEventListener('all', this.allEventHandler as EventListener);
    this.maturityAmountEntry.addEventListener('change', this.changeEventHandler as EventListener);
    this.eGui = document.createElement('div');
    this.eGui.className = 'split-order-amount-host';
    this.eGui.append(this.maturityAmountEntry);
    this.syncEntryFromParams();
  }

  refresh(params: SplitOrderAmountCellParams): boolean {
    this.params = params;
    const input = this.maturityAmountEntry.maturityInput;
    if (
      input &&
      document.activeElement === input &&
      !input.disabled &&
      this.params.data?.rowType === 'split' &&
      !this.isCapAmountApplied()
    ) {
      return false;
    }
    this.syncEntryFromParams();
    return true;
  }

  focusInput(): void {
    this.maturityAmountEntry.focusInput();
  }

  private isCapAmountApplied(): boolean {
    const { data } = this.params;
    if (!data || data.rowType === 'source') return false;
    return isFullParOrderAmountApplied({ ...data.order, QUANTITY: data.splitAmount });
  }

  private syncEntryFromParams(): void {
    const { data } = this.params;
    if (!data) return;

    this.maturityAmountEntry.gridApi = this.params.api;
    this.maturityAmountEntry.node = this.params.node;
    this.maturityAmountEntry.column = this.params.column;

    const maxAmount = this.params.getMaturityAmount?.() ?? '';
    this.maturityAmountEntry.setAttribute('max-amount', maxAmount);
    this.setEntryBoolean('show-split-button', false);
    this.setEntryBoolean('disabled', false);

    if (data.rowType === 'source') {
      this.eGui.classList.remove('is-in-transit');
      this.maturityAmountEntry.removeAttribute('data-row-id');
      this.setEntryBoolean('suppress-all-hover', true);
      this.setEntryBoolean('all-display-only', true);
      this.setEntryBoolean('amount-readonly', true);
      this.setEntryBoolean('all-active', this.params.isParentAllActive?.() ?? false);
      this.maturityAmountEntry.setAttribute(
        'value',
        this.params.getParentAmountTotal?.() ?? '',
      );
      return;
    }

    const editable = isSplitRowEditable(data);
    const capAmountApplied = this.isCapAmountApplied();
    this.eGui.classList.toggle('is-in-transit', !editable);
    this.maturityAmountEntry.dataset.rowId = data.rowId;
    this.setEntryBoolean('suppress-all-hover', false);
    this.setEntryBoolean('all-display-only', !editable);
    this.setEntryBoolean('amount-readonly', !editable || capAmountApplied);
    this.setEntryBoolean('all-active', capAmountApplied);
    this.maturityAmountEntry.setAttribute(
      'value',
      formatOrderQuantityForCell({ ...data.order, QUANTITY: data.splitAmount }),
    );
    this.applyValidationClasses(data.rowId, data.splitAmount ?? '');
  }

  private setEntryBoolean(
    attribute: 'all-active' | 'show-split-button' | 'disabled' | 'suppress-all-hover' | 'all-display-only' | 'amount-readonly',
    value: boolean,
  ): void {
    if (value) {
      this.maturityAmountEntry.setAttribute(attribute, '');
    } else {
      this.maturityAmountEntry.removeAttribute(attribute);
    }
    const propertyName = attribute.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());
    (this.maturityAmountEntry as unknown as Record<string, boolean>)[propertyName] = value;
  }

  private applyValidationClasses(rowId: string, amount: string): void {
    const state = this.params.getValidationState?.(rowId, amount);
    if (!state) return;
    applyQuantityValidationCellClasses(this.eGui.closest('.ag-cell'), state);
  }
}

export interface SplitOrderConditionsCellParams extends ICellRendererParams<SplitOrderRow> {
  onConditionsClick: (row: SplitOrderRow) => void;
  getSourceConditionsOrder: () => MaturityOrder;
}

export class SplitOrderConditionsCellRenderer implements ICellRenderer {
  private params: SplitOrderConditionsCellParams;

  private eGui: HTMLElement;

  private conditionsSummary: DealConditionsSummary | null = null;

  private clickHandler: ((e: Event) => void) | null = null;

  getGui(): HTMLElement {
    return this.eGui;
  }

  init(params: SplitOrderConditionsCellParams): void {
    this.params = params;
    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.height = '100%';
    this.eGui.style.width = '100%';
    this.eGui.style.boxSizing = 'border-box';
    this.eGui.style.padding = '0 8px';
    this.updateDisplay();
  }

  refresh(params: SplitOrderConditionsCellParams): boolean {
    this.params = params;
    this.updateDisplay();
    return true;
  }

  private updateDisplay(): void {
    const { data } = this.params;
    this.eGui.replaceChildren();
    this.eGui.style.cursor = '';

    if (this.clickHandler) {
      this.eGui.removeEventListener('click', this.clickHandler);
      this.clickHandler = null;
    }

    if (data.rowType === 'source') {
      this.appendConditionsSummary(this.params.getSourceConditionsOrder?.() ?? data.order);
      return;
    }

    const editable = isSplitRowEditable(data);
    if (editable) {
      this.eGui.style.cursor = 'pointer';
      this.clickHandler = (e: Event) => {
        e.stopPropagation();
        this.params.onConditionsClick(data);
      };
      this.eGui.addEventListener('click', this.clickHandler);
    } else {
      this.eGui.style.cursor = 'default';
    }

    if (!hasOrderConditions(data.order) && data.order.ORDER_STATUS == null) {
      return;
    }

    this.appendConditionsSummary(data.order);
  }

  private appendConditionsSummary(order: MaturityOrder): void {
    if (!this.conditionsSummary) {
      this.conditionsSummary = document.createElement(
        'deal-conditions-summary',
      ) as DealConditionsSummary;
      this.conditionsSummary.style.pointerEvents = 'none';
    }
    this.conditionsSummary.order = order;
    this.eGui.append(this.conditionsSummary);
  }
}

export interface SplitOrderRowActionsCellParams extends ICellRendererParams<SplitOrderRow> {
  onResetAmount: (rowId: string) => void;
  onClearAmount: (rowId: string) => void;
  dealStatus?: string;
  inRepriceWindow: boolean;
  dealHasEligibleOrdersToReset: boolean;
  /** Parent maturity for inheriting reconfirm eligibility on split child lines. */
  getSourceOrder?: () => MaturityOrder;
}

export class SplitOrderRowActionsCellRenderer implements ICellRenderer {
  private params: SplitOrderRowActionsCellParams;

  private eGui: HTMLElement;

  getGui(): HTMLElement {
    return this.eGui;
  }

  init(params: SplitOrderRowActionsCellParams): void {
    this.params = params;
    this.eGui = document.createElement('div');
    this.eGui.className = 'split-row-actions-host';
    this.updateDisplay();
  }

  refresh(params: SplitOrderRowActionsCellParams): boolean {
    this.params = params;
    this.updateDisplay();
    return true;
  }

  private updateDisplay(): void {
    const { data, dealStatus, inRepriceWindow, dealHasEligibleOrdersToReset } = this.params;
    this.eGui.replaceChildren();

    if (data.rowType === 'source') {
      return;
    }

    const order = data.order;
    const sourceOrder = this.params.getSourceOrder?.() ?? null;
    const rowOrder = { ...order, QUANTITY: data.splitAmount ?? order.QUANTITY };
    const hasNoQuantity = !hasOrderAmountApplied(rowOrder);
    const hasNoConditions = !hasOrderConditions(rowOrder);
    const isReset = hasNoQuantity && hasNoConditions;
    const effectiveStatus =
      order.ORDER_STATUS ??
      (String(order.ORDER_ID ?? '').trim() === '' ? OrderStatusEnum.DRAFT : undefined);

    const isReconfirmEligible = resolveSplitRowReconfirmEligible(
      order,
      sourceOrder,
      dealStatus,
    );
    const allowEditAction =
      !inRepriceWindow || (dealHasEligibleOrdersToReset && isReconfirmEligible);

    const hasSubmittedTrackBaselineEdits =
      effectiveStatus !== OrderStatusEnum.DRAFT &&
      splitRowHasEditsFromAcceptedBaseline(data);
    const showSubmittedTrackReset = inRepriceWindow
      ? isReconfirmResetTarget(rowOrder, dealStatus, sourceOrder) ||
        hasSubmittedTrackBaselineEdits
      : effectiveStatus === OrderStatusEnum.SUBMITTED_AND_DRAFT;

    if (showSubmittedTrackReset) {
      const enabled = isReconfirmRowResetEnabled(order, dealStatus, sourceOrder);
      this.eGui.append(
        this.createActionIcon(
          '/deal-icons/undo.svg',
          'Reset amount',
          !enabled,
          () => this.params.onResetAmount(data.rowId),
        ),
      );
      return;
    }

    switch (effectiveStatus) {
      case OrderStatusEnum.SUBMITTED_AND_DRAFT:
        return;
      case OrderStatusEnum.DRAFT:
        if (isReset) return;
        {
          const enabled = allowEditAction;
          this.eGui.append(
            this.createActionIcon(
              '/deal-icons/eraser.svg',
              'Clear amount',
              !enabled,
              () => this.params.onClearAmount(data.rowId),
            ),
          );
        }
        return;
    }
  }

  private createActionIcon(
    src: string,
    alt: string,
    dimmed: boolean,
    handler: () => void,
  ): HTMLImageElement {
    const icon = document.createElement('img');
    icon.src = src;
    icon.alt = alt;
    icon.className = 'split-row-action-icon';
    icon.style.height = '14px';
    icon.style.width = 'auto';
    icon.style.cursor = dimmed ? 'default' : 'pointer';
    icon.style.padding = '5px';
    icon.style.borderRadius = '5px';
    icon.style.boxSizing = 'content-box';
    if (dimmed) {
      icon.style.opacity = '0.35';
      icon.style.pointerEvents = 'none';
    }
    icon.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!dimmed) handler();
    });
    icon.addEventListener('mouseover', () => {
      if (!dimmed) icon.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
    });
    icon.addEventListener('mouseout', () => {
      icon.style.backgroundColor = 'unset';
    });
    return icon;
  }
}
